/**
 * A caixa de colaborador do banco de horas, para cadastrar e para editar.
 *
 * Papel: reproduz `#modal-overlay-bh-emp` da origem — o título "Novo
 * colaborador" ou "Editar colaborador", NOME COMPLETO em linha inteira,
 * APELIDO, FUNÇÃO, DEPARTAMENTO, ADMISSÃO e STATUS em pares, e "Cancelar" e
 * "Salvar" — e grava por `useSalvarColaboradorBancoHoras`.
 *
 * Como usar: quem monta põe o componente só enquanto a caixa está aberta, com
 * uma `key` diferente por colaborador, e o tira em `onSalvo` e em `onCancelar`.
 *
 * O cadastro é o do banco de horas, separado do quadro de Funcionários: nada
 * aqui pede CPF ou casa, e gravar aqui não muda o quadro. O apelido vazio vira
 * o primeiro nome, no schema, como `bhSaveEmployee` faz; função, departamento e
 * admissão vazios ficam vazios, e a tela escreve `—`. "Inativo" tira a pessoa
 * do resumo e da folha, e a importação continua a achá-la pelo nome.
 *
 * As recusas saem em `alert()`: o nome vazio com o texto da origem, a primeira
 * mensagem do schema (a admissão fora de `AAAA-MM-DD`) e a da API (o nome que
 * já existe no banco de horas).
 */
import {
  type ColaboradorBancoHoras,
  novoColaboradorBancoHorasSchema,
} from '@concierge/shared';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { ErroDaApi } from '@/shared/api';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useSalvarColaboradorBancoHoras } from '../hooks/usar-banco-horas';
import {
  CLASSES_DA_GRADE,
  CLASSES_DO_CAMPO,
  CLASSES_DO_GRUPO,
  CLASSES_DO_GRUPO_INTEIRO,
  CLASSES_DO_ROTULO,
  CLASSES_DO_SELECT,
  handleAbrir,
  handleImpedirFechamento,
} from './caixa-da-origem';

interface FormularioColaboradorBancoHorasProps {
  /** O colaborador a editar, ou `null` para cadastrar. */
  colaborador: ColaboradorBancoHoras | null;
  /** Chamado depois de gravar e de o banco de horas ser lido de novo. */
  onSalvo: () => void;
  /** Chamado ao clicar em "Cancelar". */
  onCancelar: () => void;
}

/** Os campos como o formulário os guarda: texto, como os campos nativos devolvem. */
interface CamposDoColaborador {
  nome: string;
  apelido: string;
  funcao: string;
  departamento: string;
  admissao: string;
  /** `'true'` ou `'false'`, como o `<select>` da origem. */
  ativo: string;
}

/** O que o `alert()` diz numa falha ao gravar que não veio da API. */
const FALHA_AO_SALVAR = 'Não foi possível salvar o colaborador.';

/**
 * Os valores com que os campos abrem.
 *
 * @param colaborador - O colaborador a editar, ou `null`.
 * @returns Vazios e "Ativo" no novo; na edição, o gravado, com nulo em branco.
 */
function valoresIniciais(colaborador: ColaboradorBancoHoras | null): CamposDoColaborador {
  if (colaborador === null) {
    return {
      nome: '',
      apelido: '',
      funcao: '',
      departamento: '',
      admissao: '',
      ativo: 'true',
    };
  }
  return {
    nome: colaborador.nomeCompleto,
    apelido: colaborador.apelido,
    funcao: colaborador.funcao ?? '',
    departamento: colaborador.departamento ?? '',
    admissao: colaborador.admissao ?? '',
    ativo: String(colaborador.ativo),
  };
}

/**
 * A caixa de colaborador do banco de horas.
 *
 * @param colaborador - O colaborador a editar, ou `null` para cadastrar.
 * @param onSalvo - Chamado depois da gravação aceita.
 * @param onCancelar - Chamado ao clicar em "Cancelar".
 * @returns O `Dialog` aberto, com os seis campos e os dois botões.
 */
export function FormularioColaboradorBancoHoras({
  colaborador,
  onSalvo,
  onCancelar,
}: FormularioColaboradorBancoHorasProps) {
  const { register, getValues } = useForm<CamposDoColaborador>({
    defaultValues: valoresIniciais(colaborador),
  });
  const salvar = useSalvarColaboradorBancoHoras();
  // "Cancelar" segue ativo durante a gravação; se ela volta depois da caixa
  // desmontada, `onSalvo` fecharia a caixa que a pessoa abriu depois.
  const montada = useRef(true);

  useEffect(() => {
    montada.current = true;
    return () => {
      montada.current = false;
    };
  }, []);

  /** Confere, monta e grava o colaborador, na ordem de `bhSaveEmployee`. */
  async function handleSalvar() {
    const valores = getValues();
    const nome = valores.nome.trim();
    if (nome === '') {
      window.alert('Informe o nome do colaborador.');
      return;
    }

    const corpo = novoColaboradorBancoHorasSchema.safeParse({
      nomeCompleto: nome,
      apelido: valores.apelido,
      funcao: valores.funcao,
      departamento: valores.departamento,
      admissao: valores.admissao.trim(),
      ativo: valores.ativo === 'true',
    });
    if (!corpo.success) {
      window.alert(corpo.error.issues[0]?.message ?? FALHA_AO_SALVAR);
      return;
    }

    try {
      await salvar.mutateAsync({ id: colaborador?.id ?? null, dados: corpo.data });
      if (montada.current) onSalvo();
    } catch (erro) {
      window.alert(erro instanceof ErroDaApi ? erro.message : FALHA_AO_SALVAR);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(aberta) => {
        if (!aberta) onCancelar();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        onOpenAutoFocus={handleAbrir}
        onEscapeKeyDown={handleImpedirFechamento}
        onPointerDownOutside={handleImpedirFechamento}
        onInteractOutside={handleImpedirFechamento}
      >
        <DialogHeader>
          <DialogTitle>
            {colaborador === null ? 'Novo colaborador' : 'Editar colaborador'}
          </DialogTitle>
        </DialogHeader>

        <div className={CLASSES_DA_GRADE}>
          <div className={CLASSES_DO_GRUPO_INTEIRO}>
            <Label htmlFor="bh-emp-nome" className={CLASSES_DO_ROTULO}>
              NOME COMPLETO
            </Label>
            <Input
              id="bh-emp-nome"
              type="text"
              className={CLASSES_DO_CAMPO}
              placeholder="Nome Sobrenome"
              {...register('nome')}
            />
          </div>

          <div className={CLASSES_DO_GRUPO}>
            <Label htmlFor="bh-emp-apelido" className={CLASSES_DO_ROTULO}>
              APELIDO
            </Label>
            <Input
              id="bh-emp-apelido"
              type="text"
              className={CLASSES_DO_CAMPO}
              placeholder="Nome"
              {...register('apelido')}
            />
          </div>

          <div className={CLASSES_DO_GRUPO}>
            <Label htmlFor="bh-emp-funcao" className={CLASSES_DO_ROTULO}>
              FUNÇÃO
            </Label>
            <Input
              id="bh-emp-funcao"
              type="text"
              className={CLASSES_DO_CAMPO}
              placeholder="Ex: Cozinheiro"
              {...register('funcao')}
            />
          </div>

          <div className={CLASSES_DO_GRUPO}>
            <Label htmlFor="bh-emp-departamento" className={CLASSES_DO_ROTULO}>
              DEPARTAMENTO
            </Label>
            <Input
              id="bh-emp-departamento"
              type="text"
              className={CLASSES_DO_CAMPO}
              placeholder="Ex: Geral"
              {...register('departamento')}
            />
          </div>

          <div className={CLASSES_DO_GRUPO}>
            <Label htmlFor="bh-emp-admissao" className={CLASSES_DO_ROTULO}>
              ADMISSÃO (AAAA-MM-DD)
            </Label>
            <Input
              id="bh-emp-admissao"
              type="text"
              className={CLASSES_DO_CAMPO}
              placeholder="2026-01-01"
              maxLength={10}
              {...register('admissao')}
            />
          </div>

          <div className={CLASSES_DO_GRUPO}>
            <Label htmlFor="bh-emp-ativo" className={CLASSES_DO_ROTULO}>
              STATUS
            </Label>
            <select id="bh-emp-ativo" className={CLASSES_DO_SELECT} {...register('ativo')}>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="text-sm/[normal]"
            onClick={onCancelar}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            className="text-sm/[normal]"
            onClick={() => void handleSalvar()}
            disabled={salvar.isPending}
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
